import { z } from "zod";

import { DRAFT_CLASSES, RECORD_TABLES, type DraftClass, type RecordTable } from "./v2-classes";
import {
  elementSchema,
  idSchema,
  jsonValueSchema,
  operationSchema,
  recordSchema,
  type DrawingRecord,
  type Element,
  type JsonValue,
  type Operation,
} from "./v2-model";
import {
  authorVarsSchema,
  drawingVariablesSchema,
  type AuthorVars,
  type DrawingVariables,
} from "./v2-variables";

/**
 * Intents, transactions and snapshots of contracts v2 (RFC 0002 §3.3, §4, §10; `orthos_model`). A client sends
 * command records; the doc service re-executes them with the same engine and logs its own operations.
 */

/** The commands the doc actor runs itself (RFC 0002 §3.1). */
export const UNDO_COMMAND = "UNDO";
export const REDO_COMMAND = "REDO";
export const OOPS_COMMAND = "OOPS";
/** The UNDO and REDO form version: 2 names several transactions; RFC 0001's `{"transaction"}` still reads. */
export const HISTORY_FORM_VERSION = 2;

/** The key of a recorded input replay, `{"$replay": {…}}`: browser only, never from the AI or the API (§3.3). */
export const REPLAY_KEY = "$replay";

/** The space a command works in: model space, a layout's paper space, or model space through a layout viewport. */
export type CommandSpace = "model" | `sheet:${string}` | `viewport:${string}`;

/** The author's context an intent carries (§3.4). */
export interface Env {
  readonly vars: AuthorVars;
  readonly space: CommandSpace;
}

/** One command's intent (FR-DRAFT-006, 007). */
export interface CommandRecord {
  readonly command: string;
  readonly version: number;
  /** The engine's JSON form, naming objects by ID, lengths in drawing units, or `{"$replay": …}`. */
  readonly json: JsonValue;
  /** IDs for what the command creates, in order (§2); the server allocates any beyond them. */
  readonly newIds?: readonly string[] | undefined;
  readonly env?: Env | undefined;
}

export type TransactionKind = "do" | "undo" | "redo";

export interface Submission {
  readonly id: string;
  readonly drawing: string;
  readonly kind: TransactionKind;
  readonly label: string;
  readonly commands: readonly CommandRecord[];
  readonly predicted: readonly Operation[];
  /** Transactions with the same group undo as one step (UNDO BEgin/End, a script, a macro). */
  readonly undoGroup?: string | undefined;
}

export interface Committed {
  readonly id: string;
  readonly drawing: string;
  readonly version: number;
  readonly author: string;
  readonly at: number;
  readonly kind: TransactionKind;
  readonly label: string;
  readonly commands: readonly CommandRecord[];
  readonly ops: readonly Operation[];
  readonly skipped?: number | undefined;
  readonly undoGroup?: string | undefined;
}

/**
 * A whole drawing in canonical form, as the protocol's `Snapshot` carries it (§10 item 6). `authorDefaults` are the
 * author variables the drawing was saved with (an imported file's CLAYER, CANNOSCALE…): creating the drawing from a
 * file sets them, no operation changes them, and a person with no author context of their own starts from them.
 */
export interface DrawingSnapshot {
  readonly drawing: string;
  readonly elements: readonly Element[];
  readonly records: SnapshotRecords;
  readonly vars: DrawingVariables;
  readonly authorDefaults: AuthorVars;
  readonly schemas: SchemaVersions;
}

/** Records by table, each table in stored order. */
export type SnapshotRecords = Readonly<Partial<Record<RecordTable, readonly DrawingRecord[]>>>;

/** The schema version of each class and table a snapshot was written with (04 §12; RFC 0002 §1.1). */
export interface SchemaVersions {
  readonly classes: Readonly<Partial<Record<DraftClass, number>>>;
  readonly tables: Readonly<Partial<Record<RecordTable, number>>>;
}

const commandSpaceSchema = z.custom<CommandSpace>(
  (text) => typeof text === "string" && /^(model|(sheet|viewport):[0-9a-f-]{36})$/.test(text),
);

export const commandRecordSchema: z.ZodType<CommandRecord> = z.strictObject({
  command: z.string(),
  version: z.number().int().min(0),
  json: jsonValueSchema,
  newIds: z.array(idSchema).optional(),
  env: z.strictObject({ vars: authorVarsSchema, space: commandSpaceSchema }).optional(),
});

const kindSchema = z.enum(["do", "undo", "redo"]);

export const submissionSchema: z.ZodType<Submission> = z.strictObject({
  id: idSchema,
  drawing: idSchema,
  kind: kindSchema,
  label: z.string(),
  commands: z.array(commandRecordSchema),
  predicted: z.array(operationSchema),
  undoGroup: idSchema.optional(),
});

export const committedSchema: z.ZodType<Committed> = z.strictObject({
  id: idSchema,
  drawing: idSchema,
  version: z.number().int().min(0),
  author: z.string(),
  at: z.number(),
  kind: kindSchema,
  label: z.string(),
  commands: z.array(commandRecordSchema),
  ops: z.array(operationSchema),
  skipped: z.number().int().min(0).optional(),
  undoGroup: idSchema.optional(),
});

export const snapshotRecordsSchema: z.ZodType<SnapshotRecords> = z.partialRecord(
  z.enum(RECORD_TABLES),
  z.array(recordSchema),
);

export const schemaVersionsSchema: z.ZodType<SchemaVersions> = z.strictObject({
  classes: z.partialRecord(z.enum(DRAFT_CLASSES), z.number().int().min(0)),
  tables: z.partialRecord(z.enum(RECORD_TABLES), z.number().int().min(0)),
});

export const snapshotSchema: z.ZodType<DrawingSnapshot> = z.strictObject({
  drawing: idSchema,
  elements: z.array(elementSchema),
  records: snapshotRecordsSchema,
  vars: drawingVariablesSchema,
  authorDefaults: authorVarsSchema,
  schemas: schemaVersionsSchema,
});

function isList(value: JsonValue): value is readonly JsonValue[] {
  return Array.isArray(value);
}

/** The recorded inputs, when a record's JSON form is a replay. */
export function replayOf(record: CommandRecord): JsonValue | undefined {
  const form = record.json;
  if (form === null || typeof form !== "object" || isList(form)) return undefined;
  const keys = Object.keys(form);
  return keys.length === 1 && keys[0] === REPLAY_KEY ? form[REPLAY_KEY] : undefined;
}

const historyForm = z.union([
  z.strictObject({ transactions: z.array(idSchema).min(1), force: z.boolean().optional() }),
  z.strictObject({ transaction: idSchema }).transform(({ transaction }) => ({
    transactions: [transaction],
    force: undefined,
  })),
]);

/** The transactions an UNDO or REDO form names: `{"transactions": [...]}`, or RFC 0001's `{"transaction"}`. */
export function historyTargets(json: JsonValue): readonly string[] | undefined {
  const form = historyForm.safeParse(json);
  return form.success ? form.data.transactions : undefined;
}
